import { navigate, withPrefix } from 'gatsby';
import { useEffect, useState } from 'react';

import useElasticSearch, { ElasticSearchResult } from './useElasticSearch';
import useIsMobile from './useIsMobile';
import { searchConfig, getElasticOptions } from '../config/searchConfig';
import {
    getRecentSearches,
    saveSearchTerm,
    SearchItem,
} from '../utils/saveSearchTerm';
import { transformElasticSuggestion } from '../utils/transformElasticResults';
import { getTopContent, TopContentSource } from '../services/topContent';

/**
 * Lógica del buscador del header: estado de apertura, búsqueda en Elasticsearch,
 * top content, búsquedas recientes y los props listos para `UniversalSearch`.
 */
const useHeaderSearch = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [topContent, setTopContent] = useState<TopContentSource | null>(null);
    const [recentSearches, setRecentSearches] = useState<SearchItem[]>([]);
    const isSm = useIsMobile();

    const { doSearch, state } =
        useElasticSearch<ElasticSearchResult>(getElasticOptions());

    // Búsqueda en tiempo real (sugerencias)
    const handleSearch = (searchValue: string) => {
        if (searchValue.trim()) {
            doSearch(searchValue);
        }
    };

    // Búsqueda completa (navega a la página de resultados)
    const handleSearchFull = (searchValue: string) => {
        if (searchValue.trim()) {
            saveSearchTerm(searchValue);
            const queryParams = new URLSearchParams({ q: searchValue });
            const newUrl = `${searchConfig.resultsRoute}?${queryParams.toString()}`;

            if (
                typeof window !== 'undefined' &&
                window.location.pathname ===
                    withPrefix(searchConfig.resultsRoute)
            ) {
                window.location.href = withPrefix(newUrl);
            } else {
                navigate(newUrl);
            }
        }
    };

    const searchSuggestions = transformElasticSuggestion(state.result || []);

    useEffect(() => {
        setRecentSearches(getRecentSearches());
    }, []);

    useEffect(() => {
        getTopContent().then((data) => setTopContent(data));
    }, []);

    const hotWords = topContent?.hot_words || [];
    const allFilters = [...recentSearches, ...hotWords];

    const mostSearchedData = topContent?.most_wanted || [];
    const mostSearched = isSm ? mostSearchedData.slice(0, 3) : mostSearchedData;

    const contentForYouData = topContent?.content_for_you || [];
    const contentForYou = isSm
        ? contentForYouData.slice(0, 3)
        : contentForYouData;

    return {
        isOpen,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        search: {
            mostSearched,
            contents: contentForYou,
            onSearch: handleSearch,
            onSearchFull: handleSearchFull,
            searchSuggestions,
            isLoadingSuggestions: state.isLoading,
            filters: allFilters,
        },
    };
};

export type HeaderSearchController = ReturnType<typeof useHeaderSearch>;

export default useHeaderSearch;
